import { inject, Injectable, signal } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import { Observable, finalize, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthResponse } from '../../../core/models/auth.reponse';
import { User } from '../../../core/models/user.model';
import { HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
    exp?: number;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService extends ApiService {

    router = inject(Router);
    private readonly refreshThresholdSeconds = 180;

    login(loginData: any): Observable<AuthResponse> {
        return this.http.post<AuthResponse>(environment.authUrl + '/token', loginData);
    }

    private refreshTokenTimeout: any;
    private refreshTokenRequest$: Observable<AuthResponse> | null = null;
    private userInfoCache$: Observable<User> | null = null;
    currentUser = signal<User | null>(null);

    refreshToken(): Observable<AuthResponse> {
        if (this.refreshTokenRequest$) {
            return this.refreshTokenRequest$;
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken || this.isTokenExpired(refreshToken)) {
            this.logout();
            return throwError(() => new Error('Refresh token expired or missing'));
        }

        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${refreshToken}`
            })
        };

        this.refreshTokenRequest$ = this.http.post<AuthResponse>(environment.authUrl + '/refreshtoken', { }, httpOptions)
            .pipe(
                tap((response) => this.setSession(response)),
                finalize(() => this.refreshTokenRequest$ = null),
                shareReplay(1)
            );

        return this.refreshTokenRequest$;
    }

    setSession(response: AuthResponse) {
        this.setToken(response.access_token);
        this.setRefreshToken(response.refresh_token);
        this.startRefreshTokenTimer();
    }

    startRefreshTokenTimer() {
        this.stopRefreshTokenTimer();

        const secondsUntilExpiration = this.getSecondsUntilTokenExpires();
        if (secondsUntilExpiration === null) {
            return;
        }

        const timeout = Math.max(
            (secondsUntilExpiration - this.refreshThresholdSeconds) * 1000,
            0
        );

        if (timeout > 0) {
            this.refreshTokenTimeout = setTimeout(() => {
                this.refreshToken().subscribe({
                    next: () => console.log('Token proactively refreshed'),
                    error: (err) => {
                        console.error('Failed to proactively refresh token', err);
                        this.logout();
                    }
                });
            }, timeout);
        }
    }

    stopRefreshTokenTimer() {
        if (this.refreshTokenTimeout) {
            clearTimeout(this.refreshTokenTimeout);
            this.refreshTokenTimeout = null;
        }
    }
   
    getUserInfo(): Observable<User> {
        if (!this.userInfoCache$) {
            const token = this.getToken();
            const headers = token
                ? new HttpHeaders({ Authorization: `Bearer ${token}` })
                : undefined;
            this.userInfoCache$ = this.http
                .get<User>(environment.authUrl + '/userinfo', headers ? { headers } : {})
                .pipe(
                    tap(userData => this.currentUser.set(userData)),
                    shareReplay(1)
                );
        }
        return this.userInfoCache$;
    }

    getToken() {
        return localStorage.getItem('icamo_accessToken');
    }

    getRefreshToken() {
        return localStorage.getItem('icamo_refreshToken');
    }

    setToken(token: string) {
        localStorage.setItem('icamo_accessToken', token);
    }

    setRefreshToken(token: string) {
        localStorage.setItem('icamo_refreshToken', token);
    }

    shouldRefreshAccessToken(): boolean {
        const secondsUntilExpiration = this.getSecondsUntilTokenExpires();
        return secondsUntilExpiration !== null && secondsUntilExpiration <= this.refreshThresholdSeconds;
    }

    getSecondsUntilTokenExpires(token = this.getToken()): number | null {
        const payload = this.decodeToken(token);
        if (!payload?.exp) {
            return null;
        }

        return payload.exp - Math.floor(Date.now() / 1000);
    }

    logout() {
        this.stopRefreshTokenTimer();
        this.userInfoCache$ = null;
        this.refreshTokenRequest$ = null;
        this.currentUser.set(null);
        localStorage.removeItem('icamo_accessToken');
        localStorage.removeItem('icamo_refreshToken');
        this.router.navigate(['/auth/login']);
    }

    initializeTokenTimer() {
        const token = this.getToken();
        if (!token) {
            return;
        }

        const secondsUntilExpiration = this.getSecondsUntilTokenExpires(token);
        if (secondsUntilExpiration === null) {
            this.logout();
            return;
        }

        if (secondsUntilExpiration <= this.refreshThresholdSeconds) {
            this.refreshToken().subscribe({
                error: () => this.logout()
            });
            return;
        }

        this.startRefreshTokenTimer();
    }

    private isTokenExpired(token: string): boolean {
        const secondsUntilExpiration = this.getSecondsUntilTokenExpires(token);
        return secondsUntilExpiration === null || secondsUntilExpiration <= 0;
    }

    private decodeToken(token: string | null): JwtPayload | null {
        if (!token) {
            return null;
        }

        try {
            return jwtDecode<JwtPayload>(token);
        } catch (e) {
            console.error('Error decoding token', e);
            return null;
        }
    }
}
